import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Trophy, 
  Edit, 
  Trash2, 
  Upload,
  Download,
  Users,
  Target,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  ChevronDown,
  ChevronRight,
  Calendar
} from 'lucide-react';
import LeagueFormModal from './LeagueFormModal';
import LeagueImportModal from './LeagueImportModal';
import LeagueScheduleImport from './LeagueScheduleImport';
import { getApiBaseUrl } from '../../utils/apiUrl';

const LeagueManagement = () => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedLeague, setExpandedLeague] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showScheduleImportModal, setShowScheduleImportModal] = useState(false);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchLeagues = useCallback(async (page = 1, search = '', status = '', sortBy = 'name', sortOrder = 'asc') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.itemsPerPage.toString(),
        search,
        status,
        sortBy,
        sortOrder
      });

      const response = await fetch(`${getApiBaseUrl()}/api/leagues?${params}`);
      const data = await response.json();

      if (data.success) {
        setLeagues(data.data.leagues || data.data);
        setPagination(data.data.pagination || {
          currentPage: page,
          totalPages: Math.ceil((data.data.leagues || data.data).length / pagination.itemsPerPage),
          totalItems: (data.data.leagues || data.data).length,
          itemsPerPage: pagination.itemsPerPage
        });
      } else {
        setError(data.error || 'Failed to fetch leagues');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [pagination.itemsPerPage]);

  useEffect(() => {
    fetchLeagues(pagination.currentPage, searchTerm, filterStatus, sortBy, sortOrder);
  }, [pagination.currentPage, searchTerm, filterStatus, sortBy, sortOrder, fetchLeagues]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleFilter = (field, value) => {
    if (field === 'status') {
      setFilterStatus(value);
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // const handleSort = (field) => {
  //   if (sortBy === field) {
  //     setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortBy(field);
  //     setSortOrder('asc');
  //   }
  // };

  const handleSelectLeague = (id) => {
    setSelectedLeagues(prev => 
      prev.includes(id) 
        ? prev.filter(l => l !== id)
        : [...prev, id]
    );
  };

  // const handleSelectAll = () => {
  //   if (selectedLeagues.length === leagues.length) {
  //     setSelectedLeagues([]);
  //   } else {
  //     setSelectedLeagues(leagues.map(l => l.id));
  //   }
  // };

  const handleDelete = async (id) => {
    if (window.confirm('คุณแน่ใจหรือไม่ที่จะลบลีกนี้? (จะทำการ soft delete)')) {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) {
          showNotification('กรุณาเข้าสู่ระบบก่อน', 'error');
          return;
        }

        const response = await fetch(`${getApiBaseUrl()}/api/leagues/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          showNotification('ลบลีกสำเร็จ', 'success');
          // Refresh the list - filter out inactive if needed
          await fetchLeagues(pagination.currentPage, searchTerm, filterStatus, sortBy, sortOrder);
        } else {
          showNotification(data.error || 'ลบลีกไม่สำเร็จ', 'error');
          console.error('Delete error:', data);
        }
      } catch (error) {
        console.error('Delete error:', error);
        showNotification('เกิดข้อผิดพลาดในการลบ: ' + error.message, 'error');
      }
    }
  };

  const handleAdd = () => {
    setEditingLeague(null);
    setShowModal(true);
  };

  const handleEdit = (league) => {
    setEditingLeague(league);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    fetchLeagues(pagination.currentPage, searchTerm, filterStatus, sortBy, sortOrder);
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleImportSuccess = () => {
    // Refresh leagues list if needed
    fetchLeagues(pagination.currentPage, searchTerm, filterStatus, sortBy, sortOrder);
  };

  const handleScheduleImportSuccess = () => {
    // Refresh leagues list after schedule import
    fetchLeagues(pagination.currentPage, searchTerm, filterStatus, sortBy, sortOrder);
    setShowScheduleImportModal(false);
  };

  const handleExport = () => {
    showNotification('กำลังส่งออกข้อมูลลีก...', 'success');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'upcoming': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'ใช้งานอยู่';
      case 'inactive': return 'ไม่ใช้งาน';
      case 'upcoming': return 'กำลังจะเปิด';
      default: return 'ไม่ทราบสถานะ';
    }
  };

  const LeagueCard = ({ league }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 ${
        selectedLeagues.includes(league.id) ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={selectedLeagues.includes(league.id)}
            onChange={() => handleSelectLeague(league.id)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          {/* League Icon */}
          <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 rounded-lg flex items-center justify-center relative">
            {league.icon ? (
              <img 
                src={`/images/leagues/${league.icon}`}
                alt={league.name}
                className="w-10 h-10 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'block';
                  }
                }}
              />
            ) : null}
            <Trophy 
              className="w-6 h-6 text-yellow-600 dark:text-yellow-400" 
              style={{ display: league.icon ? 'none' : 'block' }}
            />
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setExpandedLeague(expandedLeague === league.id ? null : league.id)}
            className="p-2 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {expandedLeague === league.id ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => handleEdit(league)}
            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(league.id)}
            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white">
            {league.name}
          </h3>
          {/* Status Badge */}
          {league.status ? (
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
              league.status === 'upcoming' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
              league.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
              league.status === 'past' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
              getStatusColor(league.is_active ? 'active' : 'inactive')
            }`}>
              {league.status === 'upcoming' ? 'Upcoming' :
               league.status === 'active' ? 'Active' :
               league.status === 'past' ? 'Past' :
               getStatusText(league.is_active ? 'active' : 'inactive')}
            </span>
          ) : (
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(league.is_active ? 'active' : 'inactive')}`}>
              {getStatusText(league.is_active ? 'active' : 'inactive')}
            </span>
          )}
          
          {/* Core League Badge */}
          {league.is_core && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
              Core
            </span>
          )}
        </div>

        {league.name_th && (
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {league.name_th}
          </p>
        )}

        {/* Ranking Count */}
        {league.ranking_count !== undefined && (
          <div className="flex items-center space-x-2 text-sm mb-2">
            <span className="text-gray-600 dark:text-gray-400">จำนวนข้อมูล:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              league.ranking_count > 0 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            }`}>
              {league.ranking_count} rankings
            </span>
          </div>
        )}

        {/* Schedule Dates */}
        {league.start_date && league.end_date && (
          <div className="flex items-center space-x-2 text-sm mb-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">
              {new Date(league.start_date).toLocaleDateString('th-TH')} - {new Date(league.end_date).toLocaleDateString('th-TH')}
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <Target className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">
              CP Limit: {league.cp_limit?.toLocaleString() || 'N/A'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">
              ผู้เล่น: {league.player_count || 0}
            </span>
          </div>
        </div>

        {league.description && (
          <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
            {league.description}
          </p>
        )}

        <AnimatePresence>
          {expandedLeague === league.id && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3"
            >
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">รายละเอียด</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {league.description || 'ไม่มีรายละเอียด'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">CP Limit:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {league.cp_limit?.toLocaleString() || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">ผู้เล่น:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {league.player_count || 0}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">สถานะ:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {getStatusText(league.is_active ? 'active' : 'inactive')}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">วันที่สร้าง:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {league.created_at ? new Date(league.created_at).toLocaleDateString('th-TH') : 'N/A'}
                  </span>
                </div>
              </div>

              {league.rules && league.rules.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">กฎกติกา</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    {league.rules.map((rule, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="w-1 h-1 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">เกิดข้อผิดพลาด</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => fetchLeagues()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-3 ${
              notification.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 hover:opacity-75"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">จัดการลีก</h1>
          <p className="text-gray-600 dark:text-gray-400">จัดการลีกและข้อมูลการแข่งขันทั้งหมด</p>
        </div>
        <div className="flex items-center space-x-3 flex-wrap">
          <button
            onClick={() => setShowScheduleImportModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Calendar className="w-4 h-4" />
            <span>Import Schedule</span>
          </button>
          <button
            onClick={handleImport}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span>นำเข้าลีก</span>
          </button>
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>ส่งออก</span>
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>เพิ่มลีก</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาลีก..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Filter className="w-4 h-4" />
              <span>ตัวกรอง</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    สถานะ
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => handleFilter('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="active">ใช้งานอยู่</option>
                    <option value="inactive">ไม่ใช้งาน</option>
                    <option value="upcoming">กำลังจะเปิด</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    เรียงตาม
                  </label>
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="name-asc">ชื่อลีก (A-Z)</option>
                    <option value="name-desc">ชื่อลีก (Z-A)</option>
                    <option value="cp_limit-asc">CP Limit (น้อยไปมาก)</option>
                    <option value="cp_limit-desc">CP Limit (มากไปน้อย)</option>
                    <option value="created_at-asc">วันที่สร้าง (เก่าไปใหม่)</option>
                    <option value="created_at-desc">วันที่สร้าง (ใหม่ไปเก่า)</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bulk Actions */}
      {selectedLeagues.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-blue-800 dark:text-blue-200 font-medium">
              เลือกแล้ว {selectedLeagues.length} รายการ
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedLeagues([])}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Leagues Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {leagues.map((league) => (
            <LeagueCard key={league.id} league={league} />
          ))}
        </AnimatePresence>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            แสดง {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} ถึง{' '}
            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} จาก{' '}
            {pagination.totalItems} รายการ
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              disabled={pagination.currentPage === 1}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ก่อนหน้า
            </button>
            <span className="px-3 py-2 text-gray-700 dark:text-gray-300">
              หน้า {pagination.currentPage} จาก {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ถัดไป
            </button>
          </div>
        </div>
      )}

      {/* League Form Modal */}
      {showModal && (
        <LeagueFormModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingLeague(null);
          }}
          league={editingLeague}
          onSuccess={handleModalSuccess}
          showNotification={showNotification}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <LeagueImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          leagues={leagues}
          onSuccess={handleImportSuccess}
          showNotification={showNotification}
        />
      )}

      {/* Schedule Import Modal */}
      {showScheduleImportModal && (
        <LeagueScheduleImport
          onSuccess={handleScheduleImportSuccess}
          onClose={() => setShowScheduleImportModal(false)}
        />
      )}
    </div>
  );
};

export default LeagueManagement;
